var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/x-wiki-draft-list-rows.ts
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element.js";
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import { css } from "@adornis/chemistry/directives/css.js";
import { XSnackbar } from "@adornis/chemistry/elements/components/x-snackbar.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import { XDialog } from "@adornis/dialog/x-dialog.js";
import { AdornisFilter } from "@adornis/filter/AdornisFilter.js";
import { StringAspect } from "@adornis/filter/aspect-schemas/StringAspect.js";
import { subscribePaginatedAndFiltered } from "@adornis/filter/db/subscribePaginatedAndFiltered.js";
import { TranslationController } from "@adornis/translation-core/client/translation-controller.js";
import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { firstValueFrom, switchMap, timer } from "rxjs";
import { WikiDraftAPI } from "../api/index.js";
import "../db/WikiEntry.js";
import { WikiEntryDraft, WikiEntryDraftStatus } from "../db/WikiEntryDraft.js";
var XWikiDraftListRows = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    /** Translation controller for wiki-specific translations */
    this.t = new TranslationController(this);
    this.referenceID = new RXController(this, "");
    this._entries = new RXController(
      this,
      this.referenceID.observable.pipe(
        switchMap((id) => {
          return subscribePaginatedAndFiltered(
            WikiEntryDraft,
            0,
            1e3,
            new AdornisFilter({
              aspects: [
                new StringAspect({
                  fieldName: "_class",
                  mode: "equals",
                  value: WikiEntryDraft._class
                }),
                new StringAspect({
                  fieldName: "referenceID",
                  mode: "equals",
                  value: id
                })
              ]
            })
          )({
            _id: 1,
            title: 1,
            createdAt: 1,
            modifiedAt: 1,
            modifiedByID: 1,
            status: 1
          });
        })
      )
    );
    this.noTitle = false;
    this.hidePublish = false;
    this.hideEdit = false;
    this.hideDelete = false;
  }
  renderActionButtons(ent) {
    const actionButtonStyle = {
      width: "24px",
      height: "24px",
      color: "#fff",
      fontSize: "14px",
      borderRadius: "8px",
      cursor: "pointer"
    };
    return xComponents(html`
      <x-flex horizontal space="xs" crossaxis-center>
        ${!this.hideEdit ? html`<x-flex
              ${css({
      ...actionButtonStyle,
      background: "#333"
    })}
              center
              crossaxis-center
              @click=${() => {
      this.dispatchEvent(
        new CustomEvent("entry-navigate", {
          detail: {
            edit: true,
            value: ent._id
          }
        })
      );
    }}
            >
              <x-icon>edit</x-icon>
            </x-flex>` : nothing}
        ${!this.hideDelete ? html`<x-flex
              ${css({
      ...actionButtonStyle,
      background: this.colors.tone.error
    })}
              center
              crossaxis-center
              @click=${async () => {
      if (!await XDialog.confirm(this.t.translate("wiki", "draftList", "confirmDelete")))
        return;
      await XDialog.waitFor(
        Promise.all([ent.remove(), firstValueFrom(timer(1e3))]),
        this.t.translate("wiki", "draftList", "deleting")
      ).then(() => {
        XSnackbar.show(this.t.translate("wiki", "draftList", "deleted"));
      });
    }}
            >
              <x-icon>delete</x-icon>
            </x-flex>` : nothing}
        ${!this.hidePublish ? html`<x-flex
              ${css({
      ...actionButtonStyle,
      background: this.colors.primary
    })}
              center
              crossaxis-center
              @click=${async () => {
      if (!await XDialog.confirm(this.t.translate("wiki", "draftList", "confirmApply")))
        return;
      await XDialog.waitFor(
        Promise.all([WikiDraftAPI.apply(ent._id)(), firstValueFrom(timer(1e3))]),
        this.t.translate("wiki", "draftList", "applying")
      ).then((res) => {
        XSnackbar.show(this.t.translate("wiki", "draftList", "applied"));
      });
    }}
            >
              <x-icon>public</x-icon>
            </x-flex>` : nothing}
      </x-flex>
    `);
  }
  render() {
    if (!this.referenceID) {
      return "";
    }
    return xComponents(html`
      <!-- Title -->
      ${this.noTitle ? nothing : xComponents(html`<x-flex
            slot="title"
            space-between
            center
            horizontal
            ${css({
      borderBottom: "1px solid rgba(0,0,0,0.1)",
      paddingBottom: this.spacing.sm,
      marginBottom: this.spacing.sm
    })}
          >
            <x-h3>${this.t.translate("wiki", "draft")}</x-h3>
          </x-flex>`)}

      <!-- Loader -->
      ${this._entries.isLoading ? html`<x-loader ${css({ width: "24px", height: "24px" })}></x-loader>` : nothing}

      <!-- Empty -->
      ${this._entries.value?.length === 0 ? html`<x-flex padding="md" space="md" center horizontal crossaxis-center>
            <x-icon>info</x-icon>
            <x-text>${this.t.translate("wiki", "draftList", "noDrafts")}</x-text>
          </x-flex>` : nothing}

      <!-- Elements -->
      <x-flex space="md">
        ${repeat(
      this._entries.value ?? [],
      (ent) => ent._id,
      (ent) => {
        return xComponents(html` <x-flex horizontal space-between>
              <x-flex space="sm">
                <x-text>${this.t.translate("wiki", "draftList", "status")}: ${WikiEntryDraftStatus[ent.status]}</x-text>
                <x-text bold>${ent.title}</x-text>
                <x-text
                  >${this.t.translate("wiki", "draftList", "createdOn")}
                  ${ent.createdAt?.toFormat("dd.MM.yyyy HH:mm")}</x-text
                >
              </x-flex>

              ${this.renderActionButtons(ent)}
            </x-flex>`);
      }
    )}
      </x-flex>
    `);
  }
};
__decorateClass([
  property({ attribute: false })
], XWikiDraftListRows.prototype, "referenceID", 2);
__decorateClass([
  state()
], XWikiDraftListRows.prototype, "_entries", 2);
__decorateClass([
  property({ type: Boolean, attribute: "no-title" })
], XWikiDraftListRows.prototype, "noTitle", 2);
__decorateClass([
  property({ type: Boolean, attribute: "hide-publish" })
], XWikiDraftListRows.prototype, "hidePublish", 2);
__decorateClass([
  property({ type: Boolean, attribute: "hide-edit" })
], XWikiDraftListRows.prototype, "hideEdit", 2);
__decorateClass([
  property({ type: Boolean, attribute: "hide-delete" })
], XWikiDraftListRows.prototype, "hideDelete", 2);
XWikiDraftListRows = __decorateClass([
  customElement("x-wiki-draft-list-rows")
], XWikiDraftListRows);
export {
  XWikiDraftListRows
};
